import React, { Dispatch, MouseEventHandler, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { getModalProps, getVariant } from './CollapsibleMenuHelpers';
import { Drawer, IconButton, useMediaQuery } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { COLLAPSIBLE_MENU_EXPAND_TIMEOUT } from '../../../../constants/CollapsibleMenu';
import { useCollapsibleMenuStyles } from './CollapsibleMenu.styles';
import { MenuVariant } from '../SideNavigationMenuHelpers';

export type CollapsibleMenuProps = {
    collapsed: boolean;
    permanent: boolean;
    isTrialActive: boolean;
    handleDrawerToggle: () => void;
    toggleCollapsed: Dispatch<SetStateAction<boolean>>;
    togglePermanent: Dispatch<SetStateAction<boolean>>;
    children: ReactNode;
    isSendBoardMicro: boolean;
};

export const CollapsibleMenu = ({
    collapsed,
    permanent,
    isTrialActive,
    handleDrawerToggle,
    children,
    toggleCollapsed,
    togglePermanent,
    isSendBoardMicro,
}: CollapsibleMenuProps) => {
    const isDesktop = useMediaQuery('@media (min-width: 1192px)');
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    const [iconVisibility, toggleIconVisibility] = useState<boolean>(false);

    useEffect(() => {
        setIsFirstRender(false);
    }, []);

    useEffect(() => {
        if (isSendBoardMicro) {
            toggleIconVisibility(true);
        } else {
            toggleIconVisibility(false);
        }
    }, [isSendBoardMicro]);

    useEffect(() => {
        if (!isFirstRender) {
            toggleCollapsed(!isDesktop);
            togglePermanent(isDesktop);
        }
    }, [isDesktop, toggleCollapsed, togglePermanent, isFirstRender]);

    const classes = useCollapsibleMenuStyles({
        collapsed,
        permanent,
        isTrialActive,
        isDesktop,
        iconVisibility,
    });

    const variant = getVariant(collapsed, permanent);

    useEffect(() => {
        if (variant === MenuVariant.permanent) {
            handleDrawerToggle();
        }
    }, [variant]);

    const ModalProps = getModalProps(collapsed, handleDrawerToggle, classes);

    const menuToggleElementRef = useRef<HTMLDivElement | null>(null);
    const [toggleCollapsedTimeout, setToggleCollapsedTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

    const clearToggleCollapsedTimeout = () => {
        if (toggleCollapsedTimeout !== null) {
            clearTimeout(toggleCollapsedTimeout);
            setToggleCollapsedTimeout(null);
        }
    };

    const toggleOnMouseEnter: MouseEventHandler<HTMLDivElement> = ({ target }) => {
        const menuElem = menuToggleElementRef.current;

        if (collapsed && target === menuElem && toggleCollapsedTimeout === null) {
            setToggleCollapsedTimeout(
                setTimeout(() => {
                    toggleCollapsed(false);
                    setToggleCollapsedTimeout(null);
                }, COLLAPSIBLE_MENU_EXPAND_TIMEOUT)
            );
        }
    };

    const onMouseEnterMenu = () => {
        if (isDesktop && !collapsed) {
            toggleIconVisibility(true);
        }
    };

    const onMouseLeaveMenu = () => {
        if (isDesktop && !collapsed) {
            toggleIconVisibility(false);
        }
    };

    const toggleOnMouseLeave: MouseEventHandler<HTMLDivElement> = () => {
        clearToggleCollapsedTimeout();
        if (!permanent) {
            toggleCollapsed(true);
        }
    };

    const togglePermanentButtonOnClick: MouseEventHandler<HTMLButtonElement> = () => {
        togglePermanent(prevState => {
            const newState = !prevState;
            toggleCollapsed(!newState);
            return newState;
        });
        clearToggleCollapsedTimeout();
    };

    return (
        <div className={classes.collapsibleMenuContainer}>
            <Drawer
                data-signa="drawer-menu"
                classes={{ paper: classes.paper }}
                PaperProps={{
                    classes: { root: classes.paper },
                }}
                variant={variant}
                anchor="left"
                open={true}
                ModalProps={ModalProps}
                onMouseEnter={onMouseEnterMenu}
                onMouseLeave={onMouseLeaveMenu}
            >
                {isDesktop && (
                    <div
                        className={classes.menuToggle}
                        onMouseEnter={toggleOnMouseEnter}
                        onMouseLeave={toggleOnMouseLeave}
                        ref={menuToggleElementRef}
                        data-signa="menu-toggle"
                    >
                        <IconButton
                            classes={{ root: classes.toggleButton }}
                            size="small"
                            onClick={togglePermanentButtonOnClick}
                            data-signa="toggle-button"
                        >
                            <ArrowForwardIosIcon
                                classes={{
                                    root: classes.toggleCollapseIcon,
                                }}
                            />
                        </IconButton>
                    </div>
                )}
                <div className={classes.children}>{children}</div>
            </Drawer>
        </div>
    );
};
